import AddIcon from '@mui/icons-material/Add';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { generateButtonSx, KEY_CARD } from './generateButtonSx';

interface KeysEmptyStateProps {
  generating: boolean;
  /** Hides the generate button (ADR-040 D5). */
  readOnly?: boolean;
  onGenerate: () => void;
}

export function KeysEmptyState({ generating, readOnly = false, onGenerate }: KeysEmptyStateProps) {
  const { t } = useTranslation('instances');
  const theme = useTheme();
  const cyan = theme.navigation.main.selectedAccent;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 3,
        borderRadius: theme.borderRadius.md,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`,
      }}
    >
      <VpnKeyIcon
        aria-hidden="true"
        sx={{ fontSize: theme.iconSize.sm, color: cyan, flexShrink: 0 }}
      />

      <Typography
        sx={{
          fontSize: theme.typography.fontSizeDescription,
          fontWeight: theme.typography.fontWeightMedium,
          color: theme.palette.text.label,
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {t('detail.section.keys')}
      </Typography>

      <Box sx={{ flex: 1, minWidth: KEY_CARD.spacerMinWidth }} />

      {!readOnly && (
        <Button
          size="small"
          variant="contained"
          disabled={generating}
          aria-busy={generating}
          startIcon={
            generating ? <CircularProgress size={theme.iconSize.xs} color="inherit" /> : <AddIcon />
          }
          onClick={onGenerate}
          sx={generateButtonSx(theme)}
        >
          {generating ? t('actions.generatingKey') : t('actions.generateKey')}
        </Button>
      )}
    </Box>
  );
}
